import type { VersionEnHistorial } from '../../api/tipos'
import { esDeAgencia } from '../../marca/marca'

/**
 * Lo que «Diseño con IA» le dice al cliente, en sus palabras (CLAUDE.md §28): ni «v12», ni
 * «bloques/Hero.astro», ni «en-cola». Funciones puras, con su prueba: la pantalla solo pinta.
 */

type Bloque = { slug: string; etiqueta: string }

/** «TarjetaDeProducto» → «tarjeta de producto». */
const enPalabras = (nombre: string) =>
  nombre
    .replace(/([a-záéíóúñ0-9])([A-ZÁÉÍÓÚÑ])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .trim()
    .toLowerCase()

const sinExtension = (tramo: string) => tramo.replace(/\.[a-z]+$/i, '')

const LAYOUTS: Record<string, string> = {
  cabecera: 'La cabecera',
  pie: 'El pie',
  base: 'La estructura de todas las páginas',
  pagina: 'La estructura de todas las páginas',
  layout: 'La estructura de todas las páginas',
}

/**
 * Qué es un fichero del tema, dicho como lo diría el cliente: «La sección «Hero»», «Colores,
 * tipografías y espaciado», «La cabecera». Las secciones se nombran con la etiqueta del
 * catálogo de bloques que manda el núcleo.
 */
export const nombreDeFichero = (ruta: string, bloques: Bloque[] = []): string => {
  if (ruta === 'tema.json') return 'Colores, tipografías y espaciado'
  if (ruta === 'estilos.css') return 'Los estilos generales'
  const tramos = ruta.split('/')
  const nombre = sinExtension(tramos[tramos.length - 1] ?? ruta)
  const carpeta = tramos[0]
  if (carpeta === 'bloques') {
    const clave = nombre.toLowerCase()
    const bloque = bloques.find((b) => b.slug.replace(/-/g, '') === clave || b.etiqueta.toLowerCase().replace(/\s+/g, '') === clave)
    return `La sección «${bloque?.etiqueta ?? enPalabras(nombre)}»`
  }
  if (carpeta === 'layouts') return LAYOUTS[nombre.toLowerCase()] ?? `La plantilla «${enPalabras(nombre)}»`
  if (carpeta === 'componentes' && tramos[1] === 'campos') return `El campo de formulario «${enPalabras(nombre)}»`
  if (carpeta === 'componentes') return `La pieza «${enPalabras(nombre)}»`
  if (carpeta === 'paginas') return `La plantilla de «${enPalabras(nombre)}»`
  if (carpeta === 'comercio') return `La tienda («${enPalabras(nombre)}»)`
  return enPalabras(nombre)
}

/** «nuevo», «cambiado», «quitado». */
export const queLePaso = (estado: string): string => (estado === 'anadido' || estado === 'añadido' ? 'nuevo' : estado === 'borrado' ? 'quitado' : 'cambiado')

/** Los cambios de una versión, en una línea: «La sección «Hero» (cambiado) · La cabecera (nuevo)». */
export const resumenDeCambios = (cambios: Array<{ ruta: string; estado: string }>, bloques: Bloque[] = []): string =>
  cambios.map((c) => `${nombreDeFichero(c.ruta, bloques)} (${queLePaso(c.estado)})`).join(' · ')

/**
 * «Tú», «Tú, con IA», «Equipo de Wixy», «Automático» o el correo de otra persona de la cuenta. En el panel
 * de una agencia revendedora (§36) nuestro equipo sale como «Soporte»: su cliente no sabe que Wixy existe.
 */
export const quienEs = (v: Pick<VersionEnHistorial, 'autor' | 'ia' | 'quien'>): string => {
  if (v.quien === 'tu') return v.ia ? 'Tú, con IA' : 'Tú'
  if (v.quien === 'equipo') return esDeAgencia() ? 'Soporte' : 'Equipo de Wixy'
  if (v.quien === 'sistema') return 'Automático'
  return v.autor ?? 'Otra persona de la cuenta'
}

export type ClaveDeEstado = 'construyendo' | 'fallida' | 'neutra' | 'publicado' | 'violeta'

/** Cómo está una versión, en llano y con el color de su pastilla. */
export const estadoDeVersion = (v: Pick<VersionEnHistorial, 'creditos' | 'descartada' | 'estado' | 'ia' | 'publicada' | 'solicitud'>): { texto: string; clave: ClaveDeEstado } => {
  if (v.publicada) return { texto: 'Publicada', clave: 'publicado' }
  if (v.estado === 'fallida') return { texto: v.ia && v.creditos === 0 ? 'No se pudo construir · no se te cobró' : 'No se pudo construir', clave: 'fallida' }
  if (v.descartada) return { texto: 'Descartada', clave: 'neutra' }
  if (v.estado === 'en-cola' || v.estado === 'construyendo') return { texto: 'Preparando la previa', clave: 'construyendo' }
  if (v.estado === 'borrador') return { texto: 'Sin preparar', clave: 'neutra' }
  if (v.solicitud?.estado === 'pendiente') return { texto: 'Esperando al equipo', clave: 'construyendo' }
  if (v.solicitud?.estado === 'rechazada') return { texto: 'Rechazada por el equipo', clave: 'fallida' }
  return { texto: 'Lista para publicar', clave: 'violeta' }
}

/**
 * Cuando no llegan los créditos: a quien puede comprarlos (`creditos.comprar`), dónde se
 * compran; a los demás, a quién pedírselos. «Pide a quien administra la cuenta» dicho a quien
 * puede comprarlos es mandarle a hablar consigo mismo.
 */
export const sinCreditos = (puedeComprar: boolean): { texto: string; enlace: null | string } =>
  puedeComprar
    ? { texto: 'No te quedan créditos suficientes para esto. Consigue más en «Créditos y plan», o espera a que se renueven los de tu plan.', enlace: 'Ir a Créditos y plan' }
    : { texto: 'No quedan créditos suficientes en la cuenta. Los consigue quien la administra: pídeselos al propietario.', enlace: null }

/** Lo que el plan no incluye, con el plan que sí lo trae. */
export const enQuePlan = (planes: null | string[] | undefined): string => {
  if (!planes || planes.length === 0) return 'No lo incluye tu plan, ni ningún otro todavía. Si lo necesitas, escríbenos.'
  const lista = planes.length === 1 ? planes[0] : `${planes.slice(0, -1).join(', ')} o ${planes[planes.length - 1]}`
  return `No lo incluye tu plan: está en ${planes.length === 1 ? 'el plan' : 'los planes'} ${lista}.`
}

/** Qué hacer con una versión que no parte de lo publicado (§18), en una frase. */
export const fraseDeConflicto = (publicada: null | Pick<VersionEnHistorial, 'comentario' | 'quien'> | undefined): string => {
  const quien = publicada?.quien === 'equipo' ? 'el equipo publicó' : publicada?.quien === 'sistema' ? 'se publicó automáticamente' : 'se publicó'
  const que = publicada?.comentario ? ` «${publicada.comentario}»` : ' otro cambio'
  return `Mientras preparabas esto, ${quien}${que}. Tu versión no lo lleva: si la publicaras tal cual, ese cambio desaparecería de tu web.`
}
